// GA4 쿠키 → 서버사이드 측정 식별자 파싱.
// 용도: 체크아웃 draft 생성 시 client_id/session_id 를 draft 에 동봉해, mgmt 가 주문 확정
// 시점에 보내는 GA4 Measurement Protocol purchase 가 원 세션(→gclid/광고 귀속)에 조인되게
// 한다(#5037). session_id 없이 client_id 만 보내면 유저에는 붙되 세션/소스에는 안 붙어
// Unassigned 로 재발하므로 둘 다 캡처한다.
//
// 쿠키는 gtag 가 세팅한 값이 정본이며, 동의 거부/광고차단기 환경에서는 쿠키 자체가 없다
// → 파싱 실패/부재 시 undefined 를 돌려주고 호출측은 필드를 생략한다(주문 생성 불파괴).
//
// 읽는 쪽은 둘이다. 파서는 공유하고 **쿠키를 꺼내오는 경로만** 다르다:
// - 자체 결제: 체크아웃 서버가 `req.cookies` 에서 (`src/libs/orders/fetchOrderDraft.ts`)
// - Global-E: 브라우저가 카트 sync 에 실어 보냄 ([readGaIdentifiersFromBrowser], #5778) —
//   GE draft 는 GE 서버가 만들어 그 시점에 쿠키가 없다.

import { getCookie } from "cookies-next";

import { GA4_ID } from "./constants";

export const GA_CLIENT_COOKIE_NAME = "_ga";

/** GA4 세션 쿠키 이름. 측정 ID G-XXXX → `_ga_XXXX`. */
export function gaSessionCookieName(measurementId: string): string {
  return `_ga_${measurementId.replace(/^G-/, "")}`;
}

/**
 * `_ga` 쿠키 → client_id.
 * 포맷: `GA1.<domainDepth>.<random>.<timestamp>` → client_id 는 마지막 두 세그먼트
 * (`<random>.<timestamp>`). domainDepth 가 1/2 로 변해도 마지막 두 세그먼트 규칙은 동일.
 */
export function parseGaClientId(
  raw: string | null | undefined,
): string | undefined {
  if (!raw || !raw.startsWith("GA1.")) return undefined;
  const parts = raw.split(".");
  if (parts.length < 4) return undefined;
  const clientId = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  return /^\d+\.\d+$/.test(clientId) ? clientId : undefined;
}

/**
 * `_ga_<컨테이너>` 쿠키 → session_id (세션 시작 unix seconds).
 * 두 세대 포맷 모두 지원:
 *   GS1: `GS1.1.<sessionId>.<count>.<engaged>....` — 세 번째 세그먼트가 그대로 sessionId
 *   GS2: `GS2.1.s<sessionId>$o<count>$g<engaged>$...` — 세 번째 세그먼트의 `s` 접두 `$` 구분
 */
export function parseGaSessionId(
  raw: string | null | undefined,
): string | undefined {
  if (!raw || !raw.startsWith("GS")) return undefined;
  const third = raw.split(".")[2];
  if (!third) return undefined;
  const head = third.split("$")[0];
  const digits = head.startsWith("s") ? head.slice(1) : head;
  return /^\d+$/.test(digits) ? digits : undefined;
}

/**
 * 브라우저에서 GA4 식별자를 읽는다 — **Global-E 카트 경유 전용**(#5778, mgmt#1276).
 *
 * GE 주문은 draft 를 GE 서버가 만들어(SendOrderToMerchant) 그 시점에 브라우저 쿠키가 없다.
 * 그래서 카트 sync 가 미리 `carts[cartToken]` 에 적어두고, mgmt 가 draft 생성 시 옮겨 싣는다.
 * 자체 결제는 이 경로를 타지 않는다 — 체크아웃 서버가 `req.cookies` 에서 직접 읽는다.
 *
 * 쿠키가 없거나(동의 거부·광고차단) 파싱에 실패하면 해당 키가 `undefined` 가 되어
 * `JSON.stringify` 에서 탈락한다. mgmt 는 **빈 값이면 기존 값을 보존**하므로, 값을 못 읽은
 * sync 가 이미 기록된 식별자를 지우지 않는다(GE 확정 시 카트 clear-sync 가 그 경우다).
 */
export function readGaIdentifiersFromBrowser(): {
  gaClientId?: string;
  gaSessionId?: string;
} {
  if (typeof document === "undefined") return {};
  const raw = (name: string) => {
    const v = getCookie(name);
    return typeof v === "string" ? v : undefined;
  };
  return {
    gaClientId: parseGaClientId(raw(GA_CLIENT_COOKIE_NAME)),
    gaSessionId: parseGaSessionId(raw(gaSessionCookieName(GA4_ID))),
  };
}
